import { useState, useCallback, useRef } from "react";
import type { ConversationState, EmailContext, Message } from "../types";
import { initialConversationState } from "../state/conversation";
import { getAccessToken } from "../office/auth";
import * as api from "../api/client";
import type { Source } from "../api/client";

function createId(): string {
  return `${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;
}

export function useConversation() {
  const [state, setState] = useState<ConversationState>(initialConversationState);
  const abortRef = useRef<AbortController | null>(null);

  const setEmailContext = useCallback((ctx: EmailContext) => {
    setState((prev) => ({ ...prev, emailContext: ctx }));
  }, []);

  const appendAssistantChunk = useCallback(
    (messageId: string, chunk: string) => {
      setState((prev) => ({
        ...prev,
        messages: prev.messages.map((m) =>
          m.id === messageId ? { ...m, content: m.content + chunk } : m
        ),
      }));
    },
    []
  );

  const finalizeAssistantMessage = useCallback(
    (messageId: string, conversationId?: string) => {
      setState((prev) => ({
        ...prev,
        conversationId: conversationId ?? prev.conversationId,
        streaming: false,
        loading: false,
        draftReady: true,
        messages: prev.messages.map((m) =>
          m.id === messageId ? { ...m, streaming: false } : m
        ),
      }));
    },
    []
  );

  const setError = useCallback((error: string) => {
    setState((prev) => ({
      ...prev,
      loading: false,
      streaming: false,
      error,
    }));
  }, []);

  const setSources = useCallback((sources: Source[]) => {
    setState((prev) => ({
      ...prev,
      sources: sources.map((s) => ({ file: s.file, title: s.title })),
    }));
  }, []);

  const generateReply = useCallback(async (instruction?: string) => {
    if (!state.emailContext) return;

    const newMessages: Message[] = [];

    // Show user's direction as a chat message if provided.
    if (instruction) {
      newMessages.push({
        id: createId(),
        role: "user",
        content: instruction,
        timestamp: new Date(),
      });
    }

    const messageId = createId();
    newMessages.push({
      id: messageId,
      role: "assistant",
      content: "",
      timestamp: new Date(),
      streaming: true,
    });

    setState((prev) => ({
      ...prev,
      loading: true,
      streaming: true,
      error: null,
      draftReady: false,
      messages: [...prev.messages, ...newMessages],
    }));

    try {
      const token = await getAccessToken();
      await api.generateReply(state.emailContext, token, {
        onChunk: (content) => appendAssistantChunk(messageId, content),
        onDone: (convId) => finalizeAssistantMessage(messageId, convId),
        onError: (err) => setError(err.message),
        onSources: setSources,
      }, instruction);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Unbekannter Fehler");
    }
  }, [state.emailContext, appendAssistantChunk, finalizeAssistantMessage, setError]);

  const refineReply = useCallback(
    async (instruction: string) => {
      if (!state.conversationId) return;

      const userMsg: Message = {
        id: createId(),
        role: "user",
        content: instruction,
        timestamp: new Date(),
      };
      const assistantId = createId();
      const assistantMsg: Message = {
        id: assistantId,
        role: "assistant",
        content: "",
        timestamp: new Date(),
        streaming: true,
      };

      setState((prev) => ({
        ...prev,
        loading: true,
        streaming: true,
        error: null,
        draftReady: false,
        messages: [...prev.messages, userMsg, assistantMsg],
      }));

      try {
        const token = await getAccessToken();
        await api.refineReply(state.conversationId, instruction, token, {
          onChunk: (content) => appendAssistantChunk(assistantId, content),
          onDone: () => finalizeAssistantMessage(assistantId),
          onError: (err) => setError(err.message),
        });
      } catch (err) {
        setError(err instanceof Error ? err.message : "Unbekannter Fehler");
      }
    },
    [state.conversationId, appendAssistantChunk, finalizeAssistantMessage, setError]
  );

  const insertDraft = useCallback(async () => {
    const lastAssistant = [...state.messages]
      .reverse()
      .find((m) => m.role === "assistant" && !m.streaming);
    if (!lastAssistant) return;

    // Convert plain text to HTML — use <br> to avoid Outlook's paragraph spacing.
    const escaped = lastAssistant.content
      .replace(/&/g, "&amp;")
      .replace(/</g, "&lt;")
      .replace(/>/g, "&gt;");
    const html = escaped.replace(/\n/g, "<br>");

    const item = Office.context?.mailbox?.item;
    if (!item) {
      setError("Kein Outlook-Kontext verfügbar");
      return;
    }

    const mode = state.emailContext?.mode ?? "read";

    if (mode === "compose") {
      // Already in a reply/compose window — set the body content.
      (item as Office.MessageCompose).body.setAsync(
        html,
        { coercionType: Office.CoercionType.Html },
        (result) => {
          if (result.status === Office.AsyncResultStatus.Failed) {
            setError(result.error?.message ?? "Text konnte nicht eingefügt werden");
          }
        }
      );
    } else {
      // Reading a mail — open a reply form with the text pre-filled.
      (item as Office.MessageRead).displayReplyForm({
        htmlBody: html,
      });
    }
  }, [state.messages, state.emailContext, setError]);

  const clearError = useCallback(() => {
    setState((prev) => ({ ...prev, error: null }));
  }, []);

  const reset = useCallback(() => {
    abortRef.current?.abort();
    setState(initialConversationState);
  }, []);

  return {
    state,
    setEmailContext,
    generateReply,
    refineReply,
    insertDraft,
    clearError,
    reset,
  };
}
